import type { Compute } from "@suse-o11y/compute";
import { isArray } from "@suse-o11y/is-array";
import type { Record } from "@suse-o11y/record";
import { toEntries } from "@suse-o11y/to-entries";

import { Failure } from "./failure";
import { Idle } from "./idle";
import { Loading } from "./loading";
import { Success } from "./success";

export type { Failure } from "./failure";
export type { Idle } from "./idle";
export type { Loading } from "./loading";
export type { Success } from "./success";

/**
 * A monad representing asynchronous data state.
 *
 * - {@link Loading}: request is in progress
 * - {@link Failure}: request failed with an error
 * - {@link Success}: request succeeded with a value
 *
 * @template TError - Failure error type.
 * @template TValue - Successful result value type.
 */
type RemoteData<TError, TValue> = Loading | ([TError] extends [never] ? never : Failure<TError>) | Success<TValue>;

const IDLE = new Idle();

/**
 * Returns the exact {@link Idle} singleton.
 */
function idle(): Idle;

/**
 * Returns an {@link Idle} | {@link RemoteData} in the idle state.
 *
 * Use this overload when the surrounding context defines `TError` and `TValue`.
 *
 * @template TError - Expected failure error type.
 * @template TValue - Expected success value type.
 */
function idle<TError, TValue>(): Idle | RemoteData<TError, TValue>;

function idle<TError = never, TValue = never>(): Idle | RemoteData<TError, TValue> {
	return IDLE;
}

const LOADING = new Loading();

/**
 * Returns the exact {@link Loading} singleton.
 */
function loading(): Loading;

/**
 * Returns an {@link RemoteData} in the loading state.
 *
 * Use this overload when the surrounding context defines `TError` and `TValue`.
 *
 * @template TError - Expected failure error type.
 * @template TValue - Expected success value type.
 */
function loading<TError, TValue>(): RemoteData<TError, TValue>;

function loading<TError = never, TValue = never>(): RemoteData<TError, TValue> {
	return LOADING;
}

/**
 * Returns a {@link Failure} instance for the given error.
 *
 * Use this overload when the error type is inferred from the provided value.
 *
 * @template TError - Failure error type (inferred).
 * @template _ForceTypeInference - Without the second type parameter, calling `failure(x)` would
 *   match the two-parameter overload and widen the return to `RemoteData` instead of `Failure`.
 *   The `extends never` constraint makes this overload unreachable with two explicit type args,
 *   so single-arg calls infer the narrow `Failure<TError>` return type.
 * @param error - The failure payload.
 */
function failure<TError, _ForceTypeInference extends never>(error: TError): Failure<TError>;

/**
 * Returns an {@link RemoteData} in the failure state.
 *
 * Use this overload when the surrounding context defines `TValue`.
 *
 * @template TError - Failure error type.
 * @template TValue - Expected success value type.
 * @param error - The failure payload.
 */
function failure<TError, TValue>(error: TError): RemoteData<TError, TValue>;

function failure<TError, TValue>(error: TError): RemoteData<TError, TValue> | Failure<TError> {
	return new Failure(error);
}

/**
 * Returns a {@link Success} instance for the given value.
 *
 * Use this overload when the value type is inferred from the provided argument.
 *
 * @template TValue - Success value type (inferred).
 * @template _ForceTypeInference - Without the second type parameter, calling `success(x)` would
 *   match the two-parameter overload and widen the return to `RemoteData` instead of `Success`.
 *   The unresolvable phantom parameter makes this overload unreachable with two explicit type args,
 *   so single-arg calls infer the narrow `Success<TValue>` return type.
 * @param value - The successful payload.
 */
function success<TValue, _ForceTypeInference>(value: TValue): Success<TValue>;

/**
 * Returns an {@link RemoteData} in the success state.
 *
 * Use this overload when the surrounding context defines `TError`.
 *
 * @template TError - Expected failure error type.
 * @template TValue - Success value type.
 * @param value - The successful payload.
 */
function success<TError, TValue>(value: TValue): RemoteData<TError, TValue>;

function success<TError, TValue>(value: TValue): RemoteData<TError, TValue> {
	return new Success(value);
}

type FallbackUnknownToNever<T> = [unknown] extends [T] ? never : T;

type SuccessOf<TRemoteData> =
	Extract<TRemoteData, Success<unknown>> extends Success<infer TValue> ? FallbackUnknownToNever<TValue> : never;
type ErrorOf<TRemoteData> =
	Extract<TRemoteData, Failure<unknown>> extends Failure<infer TError> ? FallbackUnknownToNever<TError> : never;

type SuccessAllShape<TShape extends Record<string, Idle | RemoteData<unknown, unknown>>> = Compute<
	{
		[TKey in keyof TShape]: SuccessOf<TShape[TKey]>;
	},
	"flat"
>;

type ErrorAllShape<TShape extends Record<string, Idle | RemoteData<unknown, unknown>>> = Compute<
	{
		[TKey in keyof TShape]: ErrorOf<TShape[TKey]>;
	}[keyof TShape],
	"flat"
>;

type SuccessAllArray<TArray extends ReadonlyArray<Idle | RemoteData<unknown, unknown>>> = Array<
	SuccessOf<TArray[number]>
>;
type ErrorAllArray<TArray extends ReadonlyArray<Idle | RemoteData<unknown, unknown>>> = ErrorOf<TArray[number]>;

/**
 * Combines a record of {@link RemoteData} values into a single {@link RemoteData} of a record.
 *
 * - returns {@link Failure} if any entry is failure (first failure wins)
 * - returns {@link Loading} if no failures exist and at least one entry is loading
 * - returns {@link Success} with all unwrapped values when all entries are success
 *
 * @template TShape - A record type where every value is {@link RemoteData}.
 * @param shape - The record of remote data values to combine.
 */
function all<TShape extends Record<string, RemoteData<unknown, unknown>>>(
	shape: TShape,
): RemoteData<ErrorAllShape<TShape>, SuccessAllShape<TShape>>;

/**
 * Combines a record of {@link Idle} | {@link RemoteData} values into a single {@link Idle} | {@link RemoteData} of a record.
 *
 * - returns {@link Failure} if any entry is failure (first failure wins)
 * - returns {@link Loading} if no failures exist and at least one entry is loading
 * - returns {@link Idle} if no failures or loading exist and at least one entry is idle
 * - returns {@link Success} with all unwrapped values when all entries are success
 *
 * @template TShape - A record type where every value is {@link Idle} | {@link RemoteData}.
 * @param shape - The record of remote data values to combine.
 */
function all<TShape extends Record<string, Idle | RemoteData<unknown, unknown>>>(
	shape: TShape,
): Idle | RemoteData<ErrorAllShape<TShape>, SuccessAllShape<TShape>>;

/**
 * Combines an array of {@link RemoteData} values into a single {@link RemoteData} of an array.
 *
 * - returns {@link Failure} if any entry is failure (first failure wins)
 * - returns {@link Loading} if no failures exist and at least one entry is loading
 * - returns {@link Success} with all unwrapped values when all entries are success
 *
 * @template TError - Expected failure error type.
 * @template TValue - Expected success value type.
 * @param array - The array of remote data values to combine.
 */
function all<TError, TValue>(array: ReadonlyArray<RemoteData<TError, TValue>>): RemoteData<TError, Array<TValue>>;

/**
 * Combines an array of {@link Idle} | {@link RemoteData} values into a single {@link Idle} | {@link RemoteData} of an array.
 *
 * - returns {@link Failure} if any entry is failure (first failure wins)
 * - returns {@link Loading} if no failures exist and at least one entry is loading
 * - returns {@link Idle} if no failures or loading exist and at least one entry is idle
 * - returns {@link Success} with all unwrapped values when all entries are success
 *
 * @template TError - Expected failure error type.
 * @template TValue - Expected success value type.
 * @param array - The array of remote data values to combine.
 */
function all<TError, TValue>(
	array: ReadonlyArray<Idle | RemoteData<TError, TValue>>,
): Idle | RemoteData<TError, Array<TValue>>;

/**
 * Combines an array of {@link RemoteData} values into a single {@link RemoteData} of an array.
 *
 * - returns {@link Failure} if any entry is failure (first failure wins)
 * - returns {@link Loading} if no failures exist and at least one entry is loading
 * - returns {@link Success} with all unwrapped values when all entries are success
 *
 * @template TArray - A readonly array type where every element is {@link RemoteData}.
 * @param array - The array of remote data values to combine.
 */
function all<TArray extends ReadonlyArray<RemoteData<unknown, unknown>>>(
	array: TArray,
): RemoteData<ErrorAllArray<TArray>, SuccessAllArray<TArray>>;

/**
 * Combines an array of {@link Idle} | {@link RemoteData} values into a single {@link Idle} | {@link RemoteData} of an array.
 *
 * - returns {@link Failure} if any entry is failure (first failure wins)
 * - returns {@link Loading} if no failures exist and at least one entry is loading
 * - returns {@link Idle} if no failures or loading exist and at least one entry is idle
 * - returns {@link Success} with all unwrapped values when all entries are success
 *
 * @template TArray - A readonly array type where every element is {@link Idle} | {@link RemoteData}.
 * @param array - The array of remote data values to combine.
 */
function all<TArray extends ReadonlyArray<Idle | RemoteData<unknown, unknown>>>(
	array: TArray,
): Idle | RemoteData<ErrorAllArray<TArray>, SuccessAllArray<TArray>>;

function all(
	shapeOrArray:
		| Record<string, Idle | RemoteData<unknown, unknown>>
		| ReadonlyArray<Idle | RemoteData<unknown, unknown>>,
): Idle | RemoteData<unknown, unknown> {
	if (isArray(shapeOrArray)) {
		const array: Array<unknown> = [];
		const result = allFromEntries((value) => array.push(value), shapeOrArray.entries());

		return result ?? new Success(array);
	}

	const shape: Record<string, unknown, "writable"> = {};
	const result = allFromEntries((value, key) => {
		shape[key] = value;
	}, toEntries(shapeOrArray));

	return result ?? new Success(shape);
}

function allFromEntries<K>(
	cb: (value: unknown, key: K) => void,
	entries: Iterable<[K, Idle | RemoteData<unknown, unknown>]>,
) {
	let hasLoading = false;
	let hasIdle = false;

	for (const [key, entry] of entries) {
		if (entry.isFailure()) {
			return entry;
		}

		if (entry.isLoading()) {
			hasLoading = true;
		} else if (entry.isIdle()) {
			hasIdle = true;
		} else {
			cb(entry.value, key);
		}
	}

	if (hasLoading) {
		return LOADING;
	}

	if (hasIdle) {
		return IDLE;
	}

	return null;
}

/**
 * Factory methods for creating {@link RemoteData} variants.
 */
const RemoteData = { idle, loading, failure, success, all };

export type { ErrorAllArray, ErrorAllShape, SuccessAllArray, SuccessAllShape };
export { RemoteData };
